import React, { useEffect, useState, useCallback } from 'react';
import Header from '../../components/layout/Header';
import { Button } from '../../components/common/Common';
import { Plus, Pencil, Trash2, Power, X, Eye, Info, AlertTriangle } from 'lucide-react';
import { useCRM } from '../../context/CRMContext';
import { useToast } from '../../context/ToastContext';
import { apiFetch } from '../../utils/api';
import './LeadScheduling.css';

const EMPTY_FORM = {
  schedule_type: '',
  user_role: '',
  user_ids: [],
  date_from: '',
  date_to: '',
  source_ids: [],
  service_type_ids: [],
  project_ids: [],
  form_names: [],
  cities: [],
  status: 'active',
};

const LeadScheduling = () => {
  const { masterData, fetchUsers } = useCRM();
  const { showToast } = useToast();

  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [cityInput, setCityInput] = useState('');
  const [viewingSchedule, setViewingSchedule] = useState(null);

  const sources = masterData?.sources || [];
  const serviceTypes = masterData?.service_types || [];
  const projects = masterData?.projects || [];
  const formNames = masterData?.form_names || [];

  // Distinct roles derived from users list
  const roles = Array.from(
    new Map(
      users
        .filter(u => u.role_slug && u.role_name)
        .map(u => [u.role_slug, { slug: u.role_slug, name: u.role_name }])
    ).values()
  );

  const usersOfSelectedRole = form.user_role
    ? users.filter(u => u.role_slug === form.user_role)
    : [];

  const loadSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('lead-scheduling');
      if (res?.success) setSchedules(res.data?.schedules || []);
    } catch (_) {}
    setLoading(false);
  }, []);

  const loadUsers = useCallback(async () => {
    const res = await fetchUsers();
    if (res?.success) setUsers(res.data?.users || res.data || []);
  }, [fetchUsers]);

  useEffect(() => {
    loadSchedules();
    loadUsers();
  }, [loadSchedules, loadUsers]);

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setCityInput('');
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditingId(s.ls_id);
    const editUserIds = (s.users || []).map(u => u.u_id);
    // Infer role from the first assigned user
    const firstUser = users.find(u => u.u_id === editUserIds[0]);
    setForm({
      schedule_type: s.schedule_type || '',
      user_role: firstUser?.role_slug || '',
      user_ids: editUserIds,
      date_from: s.date_from ? new Date(s.date_from).toISOString().slice(0, 10) : '',
      date_to: s.date_to ? new Date(s.date_to).toISOString().slice(0, 10) : '',
      source_ids: s.source_ids || [],
      service_type_ids: s.service_type_ids || [],
      project_ids: s.project_ids || [],
      form_names: s.form_names || [],
      cities: s.cities || [],
      status: s.status || 'active',
    });
    setCityInput('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const toggleArrayValue = (key, value) => {
    setForm(prev => {
      const arr = prev[key] || [];
      const idx = arr.indexOf(value);
      const next = idx === -1 ? [...arr, value] : arr.filter(v => v !== value);
      return { ...prev, [key]: next };
    });
  };

  const setUserIds = (val) => {
    if (form.schedule_type === 'manual') {
      setForm(prev => ({ ...prev, user_ids: val ? [Number(val)] : [] }));
    }
  };

  const addCity = () => {
    const v = cityInput.trim();
    if (!v) return;
    if (form.cities.includes(v)) { setCityInput(''); return; }
    setForm(prev => ({ ...prev, cities: [...prev.cities, v] }));
    setCityInput('');
  };

  const removeCity = (c) => {
    setForm(prev => ({ ...prev, cities: prev.cities.filter(x => x !== c) }));
  };

  const submit = async () => {
    if (!form.schedule_type) { showToast('Select a schedule type', 'error'); return; }
    if (!form.user_role) { showToast('Select a user role', 'error'); return; }
    if (!form.user_ids || form.user_ids.length === 0) { showToast('Select at least one user', 'error'); return; }
    if (form.schedule_type === 'manual' && form.user_ids.length > 1) { showToast('Manual schedule allows only one user', 'error'); return; }
    if (!form.status) { showToast('Select a status', 'error'); return; }

    setSubmitting(true);
    try {
      const url = editingId ? `lead-scheduling/${editingId}` : 'lead-scheduling';
      const method = editingId ? 'PUT' : 'POST';
      const res = await apiFetch(url, { method, body: JSON.stringify(form) });
      if (res?.success) {
        showToast(editingId ? 'Schedule updated' : 'Schedule created', 'success');
        closeForm();
        loadSchedules();
      } else {
        showToast(res?.message || 'Failed to save', 'error');
      }
    } catch (e) {
      showToast('Failed to save', 'error');
    }
    setSubmitting(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;
    try {
      const res = await apiFetch(`lead-scheduling/${id}`, { method: 'DELETE' });
      if (res?.success) { showToast('Deleted', 'success'); loadSchedules(); }
    } catch (_) {}
  };

  const toggleStatus = async (id) => {
    try {
      const res = await apiFetch(`lead-scheduling/${id}/toggle`, { method: 'PATCH' });
      if (res?.success) loadSchedules();
    } catch (_) {}
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
  const formatDateTime = (d) => d ? new Date(d).toLocaleString() : '—';

  const namesFromIds = (ids, list, idKey, nameKey = 'name') => {
    if (!ids || ids.length === 0) return [];
    return ids
      .map(id => list.find(item => item[idKey] === id))
      .filter(Boolean)
      .map(item => item[nameKey]);
  };

  const renderListCell = (items) => {
    if (!items || items.length === 0) return <span className="ls-cell-dash">—</span>;
    const full = items.join(', ');
    const preview = items.length === 1 ? items[0] : `${items[0]} +${items.length - 1}`;
    return <span className="ls-cell-list" title={full}>{preview}</span>;
  };

  const getScheduleConditions = (s) => {
    const conds = [];
    if (s.date_from || s.date_to) {
      const from = s.date_from ? formatDate(s.date_from) : '∞';
      const to = s.date_to ? formatDate(s.date_to) : '∞';
      conds.push({ label: 'Date', value: `${from} → ${to}` });
    }
    const sourceNames = namesFromIds(s.source_ids, sources, 'src_id');
    if (sourceNames.length > 0) conds.push({ label: 'Source', value: sourceNames.join(', ') });
    const stNames = namesFromIds(s.service_type_ids, serviceTypes, 'st_id');
    if (stNames.length > 0) conds.push({ label: 'Service Type', value: stNames.join(', ') });
    const projectNames = namesFromIds(s.project_ids, projects, 'project_id');
    if (projectNames.length > 0) conds.push({ label: 'Project', value: projectNames.join(', ') });
    if (s.form_names && s.form_names.length > 0) conds.push({ label: 'Form', value: s.form_names.join(', ') });
    if (s.cities && s.cities.length > 0) conds.push({ label: 'City', value: s.cities.join(', ') });
    return conds;
  };

  // Two schedulers conflict if a single lead can satisfy ALL filters of BOTH.
  // For each filter dimension both have, their values must overlap; otherwise no conflict.
  const detectConflict = (a, b) => {
    if (a.status !== 'active' || b.status !== 'active') return null;
    const shared = [];

    const dims = [
      { aVals: a.source_ids || [], bVals: b.source_ids || [], label: 'Source', resolve: (ids) => namesFromIds(ids, sources, 'src_id') },
      { aVals: a.service_type_ids || [], bVals: b.service_type_ids || [], label: 'Service Type', resolve: (ids) => namesFromIds(ids, serviceTypes, 'st_id') },
      { aVals: a.project_ids || [], bVals: b.project_ids || [], label: 'Project', resolve: (ids) => namesFromIds(ids, projects, 'project_id') },
      { aVals: a.form_names || [], bVals: b.form_names || [], label: 'Form', resolve: (v) => v },
      { aVals: a.cities || [], bVals: b.cities || [], label: 'City', resolve: (v) => v, ci: true },
    ];

    for (const d of dims) {
      if (d.aVals.length === 0 || d.bVals.length === 0) continue;
      let overlap;
      if (d.ci) {
        const bLower = d.bVals.map(v => String(v).toLowerCase());
        overlap = d.aVals.filter(v => bLower.includes(String(v).toLowerCase()));
      } else {
        overlap = d.aVals.filter(v => d.bVals.includes(v));
      }
      if (overlap.length === 0) return null;
      shared.push({ label: d.label, values: d.resolve(overlap) });
    }

    return { shared };
  };

  return (
    <div>
      <Header
        title="Lead Scheduling"
        subtitle="Auto-assign incoming leads based on rules"
        actions={<Button variant="gold" icon={Plus} onClick={openAdd}>Add Scheduler</Button>}
      />

      <div className="page">
        {showForm && (
          <div className="ls-card ls-card--form">
            <div className="ls-form__header">
              <h3>{editingId ? 'Edit Scheduler' : 'Add Scheduler'}</h3>
              <button className="ls-icon-btn" onClick={closeForm} aria-label="Close"><X size={18} /></button>
            </div>

            <div className="ls-grid">
              <div className="ls-field">
                <label>Schedule Type *</label>
                <select
                  value={form.schedule_type}
                  onChange={(e) => setForm(prev => ({ ...prev, schedule_type: e.target.value, user_ids: [] }))}
                >
                  <option value="">Select a Schedule Type</option>
                  <option value="manual">Normal Scheduler</option>
                  <option value="round_robin">Round Robin Scheduler</option>
                </select>
              </div>

              <div className="ls-field">
                <label>User Role *</label>
                <select
                  value={form.user_role}
                  onChange={(e) => setForm(prev => ({ ...prev, user_role: e.target.value, user_ids: [] }))}
                >
                  <option value="">Select a User Role</option>
                  {roles.map(r => <option key={r.slug} value={r.slug}>{r.name}</option>)}
                </select>
              </div>

              <div className="ls-field">
                <label>Lead Date From</label>
                <input type="date" value={form.date_from} onChange={(e) => setForm(prev => ({ ...prev, date_from: e.target.value }))} />
              </div>

              <div className="ls-field">
                <label>Lead Date To</label>
                <input type="date" value={form.date_to} onChange={(e) => setForm(prev => ({ ...prev, date_to: e.target.value }))} />
              </div>

              <div className="ls-field ls-field--full">
                <label>
                  {form.user_role
                    ? `${roles.find(r => r.slug === form.user_role)?.name || 'User'} *`
                    : 'User *'}
                  {' '}
                  {form.schedule_type === 'manual' ? '(select one)' : form.schedule_type === 'round_robin' ? '(select multiple — round-robin)' : ''}
                </label>
                {!form.schedule_type || !form.user_role ? (
                  <span className="ls-muted">Select Schedule Type and User Role first</span>
                ) : form.schedule_type === 'manual' ? (
                  <select value={form.user_ids[0] || ''} onChange={(e) => setUserIds(e.target.value)}>
                    <option value="">{`Select a ${roles.find(r => r.slug === form.user_role)?.name || 'User'}`}</option>
                    {usersOfSelectedRole.map(u => <option key={u.u_id} value={u.u_id}>{u.username}</option>)}
                  </select>
                ) : (
                  <div className="ls-checkrow">
                    {usersOfSelectedRole.length === 0 && <span className="ls-muted">No users in this role</span>}
                    {usersOfSelectedRole.map(u => (
                      <label key={u.u_id} className="ls-chip">
                        <input
                          type="checkbox"
                          checked={form.user_ids.includes(u.u_id)}
                          onChange={() => toggleArrayValue('user_ids', u.u_id)}
                        />
                        <span>{u.username}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="ls-field">
                <label>Status *</label>
                <select value={form.status} onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="ls-field ls-field--full">
                <label>Sources</label>
                <div className="ls-checkrow">
                  {sources.map(s => (
                    <label key={s.src_id} className="ls-chip">
                      <input
                        type="checkbox"
                        checked={form.source_ids.includes(s.src_id)}
                        onChange={() => toggleArrayValue('source_ids', s.src_id)}
                      />
                      <span>{s.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="ls-field ls-field--full">
                <label>Service Types</label>
                <div className="ls-checkrow">
                  {serviceTypes.map(st => (
                    <label key={st.st_id} className="ls-chip">
                      <input
                        type="checkbox"
                        checked={form.service_type_ids.includes(st.st_id)}
                        onChange={() => toggleArrayValue('service_type_ids', st.st_id)}
                      />
                      <span>{st.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="ls-field ls-field--full">
                <label>Projects</label>
                <div className="ls-checkrow">
                  {projects.map(p => (
                    <label key={p.project_id} className="ls-chip">
                      <input
                        type="checkbox"
                        checked={form.project_ids.includes(p.project_id)}
                        onChange={() => toggleArrayValue('project_ids', p.project_id)}
                      />
                      <span>{p.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="ls-field ls-field--full">
                <label>Form Names</label>
                {formNames.length === 0 ? (
                  <span className="ls-muted">No form names available</span>
                ) : (
                  <div className="ls-checkrow">
                    {formNames.map(fn => (
                      <label key={fn} className="ls-chip">
                        <input
                          type="checkbox"
                          checked={form.form_names.includes(fn)}
                          onChange={() => toggleArrayValue('form_names', fn)}
                        />
                        <span>{fn}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="ls-field ls-field--full">
                <label>Cities</label>
                <div className="ls-city-input">
                  <input
                    type="text"
                    placeholder="Type a city and press Enter"
                    value={cityInput}
                    onChange={(e) => setCityInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCity(); } }}
                  />
                  <button type="button" onClick={addCity}>Add</button>
                </div>
                {form.cities.length > 0 && (
                  <div className="ls-tags">
                    {form.cities.map(c => (
                      <span key={c} className="ls-tag">
                        {c}
                        <button type="button" onClick={() => removeCity(c)}><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="ls-form__actions">
              <Button variant="ghost" onClick={closeForm}>Cancel</Button>
              <Button variant="gold" onClick={submit} disabled={submitting}>
                {submitting ? 'Saving...' : (editingId ? 'Update Scheduler' : 'Add Scheduler')}
              </Button>
            </div>
          </div>
        )}

        <div className="ls-disclaimer">
          <Info size={16} />
          <div>
            <strong>How a lead picks its scheduler:</strong> when an incoming lead matches more than one scheduler, the one with the <strong>most matching conditions</strong> wins (most specific). If two schedulers have the same number of matches, the one with the lower Sr. No. wins. A scheduler with no conditions is a catch-all fallback.

            {schedules.length > 0 && (
              <div className="ls-disclaimer__detail">
                <strong>Your current setup:</strong>
                <ul className="ls-disclaimer__list">
                  {schedules.map((s, i) => {
                    const conds = getScheduleConditions(s);
                    const userNames = (s.users || []).map(u => u.username).join(', ') || <em>no users</em>;
                    // Find conflicts with other active schedulers
                    const conflicts = [];
                    schedules.forEach((other, j) => {
                      if (j === i) return;
                      const c = detectConflict(s, other);
                      if (c) {
                        const otherConds = getScheduleConditions(other);
                        let outcome;
                        if (conds.length > otherConds.length) outcome = 'this one wins';
                        else if (conds.length < otherConds.length) outcome = `Sr. No. ${j + 1} wins`;
                        else outcome = i < j ? 'this one wins' : `Sr. No. ${j + 1} wins (older)`;
                        conflicts.push({ idx: j + 1, shared: c.shared, outcome, otherCount: otherConds.length });
                      }
                    });
                    return (
                      <li key={s.ls_id}>
                        <strong>Sr. No. {i + 1}</strong>
                        {s.status !== 'active' && <span className="ls-disclaimer__inactive">Inactive</span>}
                        <span className="ls-disclaimer__count">{conds.length} condition{conds.length === 1 ? '' : 's'}</span>
                        <span className="ls-disclaimer__arrow">→</span>
                        <strong>{userNames}</strong>
                        {conds.length > 0 ? (
                          <div className="ls-disclaimer__conds">
                            {conds.map((c, idx) => (
                              <span key={idx} className="ls-disclaimer__cond"><span className="ls-disclaimer__cond-label">{c.label}:</span> {c.value}</span>
                            ))}
                          </div>
                        ) : (
                          <div className="ls-disclaimer__conds"><em>No conditions — catches every lead.</em></div>
                        )}
                        {conflicts.map((cf, k) => (
                          <div key={k} className="ls-disclaimer__conflict">
                            <AlertTriangle size={13} />
                            <span>
                              <strong>Conflicts with Sr. No. {cf.idx}</strong>
                              {cf.shared.length > 0 && (
                                <>: shares {cf.shared.map((s2, idx2) => (
                                  <span key={idx2}>
                                    {idx2 > 0 && ', '}
                                    <strong>{s2.label} = {s2.values.join(', ')}</strong>
                                  </span>
                                ))}</>
                              )}
                              {cf.shared.length === 0 && ' (any lead satisfying both rule-sets matches both)'}
                              {' — '}<em>{cf.outcome}</em>
                              {cf.outcome.includes('wins') && (
                                <span className="ls-disclaimer__conflict-count">
                                  {' '}({conds.length} vs {cf.otherCount} condition{cf.otherCount === 1 ? '' : 's'})
                                </span>
                              )}
                            </span>
                          </div>
                        ))}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="ls-card">
          <div className="ls-list-head">
            <h3>{schedules.length} schedule{schedules.length === 1 ? '' : 's'}</h3>
          </div>

          <div className="ls-table-wrap">
            <table className="ls-table">
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Actions</th>
                  {/* <th>Posted On</th> */}
                  {/* <th>Created By</th> */}
                  <th>Schedule Type</th>
                  <th>Assign To</th>
                  <th>Lead Date From</th>
                  <th>Lead Date To</th>
                  <th>Source</th>
                  <th>Service Type</th>
                  <th>Project Name</th>
                  <th>Form Name</th>
                  <th>City</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={12} className="ls-empty">Loading...</td></tr>
                )}
                {!loading && schedules.length === 0 && (
                  <tr><td colSpan={12} className="ls-empty">No schedulers yet. Click "Add Scheduler".</td></tr>
                )}
                {schedules.map((s, i) => {
                  const userNames = (s.users || []).map(u => u.username);
                  const sourceNames = namesFromIds(s.source_ids, sources, 'src_id');
                  const stNames = namesFromIds(s.service_type_ids, serviceTypes, 'st_id');
                  const projectNames = namesFromIds(s.project_ids, projects, 'project_id');
                  return (
                    <tr key={s.ls_id}>
                      <td>{i + 1}</td>
                      <td>
                        <div className="ls-actions">
                          <button className="ls-icon-btn" title="View details" onClick={() => setViewingSchedule(s)}><Eye size={16} /></button>
                          <button className="ls-icon-btn" title={s.status === 'active' ? 'Deactivate' : 'Activate'} onClick={() => toggleStatus(s.ls_id)}><Power size={16} /></button>
                          <button className="ls-icon-btn" title="Edit" onClick={() => openEdit(s)}><Pencil size={16} /></button>
                          <button className="ls-icon-btn ls-icon-btn--danger" title="Delete" onClick={() => remove(s.ls_id)}><Trash2 size={16} /></button>
                        </div>
                      </td>
                      {/* <td title={formatDateTime(s.created_at)}>{formatDateTime(s.created_at)}</td> */}
                      {/* <td>{s.created_by_name || '—'}</td> */}
                      <td>
                        <span className={`ls-pill ls-pill--${s.schedule_type}`}>
                          {s.schedule_type === 'round_robin' ? 'Round Robin' : 'Normal'}
                        </span>
                      </td>
                      <td>{renderListCell(userNames)}</td>
                      <td>{formatDate(s.date_from)}</td>
                      <td>{formatDate(s.date_to)}</td>
                      <td>{renderListCell(sourceNames)}</td>
                      <td>{renderListCell(stNames)}</td>
                      <td>{renderListCell(projectNames)}</td>
                      <td>{renderListCell(s.form_names)}</td>
                      <td>{renderListCell(s.cities)}</td>
                      <td>
                        <span className={`ls-status ls-status--${s.status}`}>{s.status}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {viewingSchedule && (
          <div className="ls-modal" onClick={() => setViewingSchedule(null)}>
            <div className="ls-modal__content" onClick={(e) => e.stopPropagation()}>
              <div className="ls-modal__header">
                <h3>Scheduler #{viewingSchedule.ls_id}</h3>
                <button className="ls-icon-btn" onClick={() => setViewingSchedule(null)} aria-label="Close"><X size={18} /></button>
              </div>
              <div className="ls-modal__body">
                <div className="ls-detail">
                  <span className="ls-detail__label">Posted On</span>
                  <span className="ls-detail__value">{formatDateTime(viewingSchedule.created_at)}</span>
                </div>
                <div className="ls-detail">
                  <span className="ls-detail__label">Created By</span>
                  <span className="ls-detail__value">{viewingSchedule.created_by_name || '—'}</span>
                </div>
                <div className="ls-detail">
                  <span className="ls-detail__label">Schedule Type</span>
                  <span className="ls-detail__value">
                    <span className={`ls-pill ls-pill--${viewingSchedule.schedule_type}`}>
                      {viewingSchedule.schedule_type === 'round_robin' ? 'Round Robin' : 'Normal'}
                    </span>
                  </span>
                </div>
                <div className="ls-detail">
                  <span className="ls-detail__label">Status</span>
                  <span className="ls-detail__value">
                    <span className={`ls-status ls-status--${viewingSchedule.status}`}>{viewingSchedule.status}</span>
                  </span>
                </div>
                <div className="ls-detail">
                  <span className="ls-detail__label">Assigned To</span>
                  <span className="ls-detail__value">{(viewingSchedule.users || []).map(u => u.username).join(', ') || '—'}</span>
                </div>
                <div className="ls-detail">
                  <span className="ls-detail__label">Lead Date Range</span>
                  <span className="ls-detail__value">{formatDate(viewingSchedule.date_from)} — {formatDate(viewingSchedule.date_to)}</span>
                </div>
                <div className="ls-detail">
                  <span className="ls-detail__label">Sources</span>
                  <span className="ls-detail__value">{namesFromIds(viewingSchedule.source_ids, sources, 'src_id').join(', ') || '—'}</span>
                </div>
                <div className="ls-detail">
                  <span className="ls-detail__label">Service Types</span>
                  <span className="ls-detail__value">{namesFromIds(viewingSchedule.service_type_ids, serviceTypes, 'st_id').join(', ') || '—'}</span>
                </div>
                <div className="ls-detail">
                  <span className="ls-detail__label">Projects</span>
                  <span className="ls-detail__value">{namesFromIds(viewingSchedule.project_ids, projects, 'project_id').join(', ') || '—'}</span>
                </div>
                <div className="ls-detail">
                  <span className="ls-detail__label">Form Names</span>
                  <span className="ls-detail__value">{(viewingSchedule.form_names || []).join(', ') || '—'}</span>
                </div>
                <div className="ls-detail">
                  <span className="ls-detail__label">Cities</span>
                  <span className="ls-detail__value">{(viewingSchedule.cities || []).join(', ') || '—'}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadScheduling;
